import type { MeasureUnit } from '@mfw/common';

import {
  canConvertUnits,
  getConvertedValueForDB,
  getPreferredUnitForUi,
  ConfigurableFormat,
} from '@mfw/common';
import { getLocalizedNameByLocale } from '@mfw/components';
import omit from 'lodash/omit';

import type {
  UIGroceryCategories,
  ApiGrocery,
  UiGrocery,
} from '../../schemas/groceries.schema';
import type { UserUnitSettings } from '../../../../@mfw-common/enums';
import type { CreateGroceryArgs } from '../types/groceries.types';
import type { Locale } from '../../../../@mfw-translations';

import { getConvertedValueForUi } from '../../../../@mfw-common/enums';
import { mapTranslationsFromApi } from './translations.mappers';
import { getLabelFromTranslations } from '../../utils/locales';

export function mapGroceryFromApi(
  grocery: ApiGrocery,
  locale: string,
  userUnitSettings?: UserUnitSettings | null,
): UiGrocery {
  const categories: UIGroceryCategories = {};
  if (grocery.categories && grocery.categories.length) {
    grocery.categories.forEach((category) => {
      categories[category.id] = category.tags.map((tag) => {
        return {
          label: getLabelFromTranslations(
            locale as Locale,
            mapTranslationsFromApi(tag.translations),
          ),
          value: tag.id,
        };
      });
    });
  }

  return {
    ...grocery,
    categories,
    coverSignedUrl: grocery.coverSignedUrl ?? null,
    groceryType: grocery.groceryType,
    isDeleted: grocery.isDeleted ?? false,
    nutritionValues: (grocery.nutritionValues ?? []).map((nv) => ({
      name: nv.name,
      unit: [
        {
          label:
            getPreferredUnitForUi(
              (nv.unit ?? 'g') as MeasureUnit,
              userUnitSettings,
            ) ??
            nv.unit ??
            'g',
          value:
            getPreferredUnitForUi(
              (nv.unit ?? 'g') as MeasureUnit,
              userUnitSettings,
            ) ??
            nv.unit ??
            'g',
        },
      ],
      // the STORED unit survives the display rewrite — see `dbUnit`
      dbUnit: (nv.unit ?? 'g') as MeasureUnit,
      value: +getConvertedValueForUi({
        format: NUTRITION_VALUE_FORMAT,
        fromUnit: (nv.unit ?? 'g') as MeasureUnit,
        toUnit: getPreferredUnitForUi(
          (nv.unit ?? 'g') as MeasureUnit,
          userUnitSettings,
        ),
        value: nv.value,
      }),
    })),
    translations: (grocery.translations ?? []).map((t) => ({
      description: t.description ?? '',
      locale: [{ label: getLocalizedNameByLocale(t.locale), value: t.locale }],
      name: t.name,
    })),
    uniqueId: grocery.id,
  };
}

const UNIT_MAP: Record<string, 'g' | 'kcal' | 'mg'> = {
  g: 'g',
  kJ: 'kcal',
  kcal: 'kcal',
  lb: 'g',
  mg: 'mg',
  oz: 'g',
};

/**
 * THE UNIT COLUMN AND THE VALUE ARE ONE DECISION — same defect and same fix as
 * `meals.mappers.ts`. The save wrote `UNIT_MAP[uiUnit]` into `unit` while
 * converting with no `dbUnit`, so the number never followed the label: an
 * ounce reading was stored as if it were grams.
 *
 * `NUMBER` was also the wrong precision for the load: 25 g shown to an imperial
 * reader is 0.88 oz, rounded to 1, and saved back as 28 g. Both directions use
 * two decimals now, which is what the input accepts anyway.
 */
const nutritionValueDbUnit = (uiUnit: MeasureUnit) =>
  (UNIT_MAP[uiUnit] ?? 'g') as MeasureUnit;

/**
 * The stored unit when the pick is still in its dimension, the pick's own
 * storage spelling when it is not — see `mealQuantityTargetUnit` in
 * `meals.mappers.ts` for the full reasoning.
 *
 * `UNIT_OPTIONS` here spans MASS and ENERGY (`g · mg · lb · oz · kcal · kJ`),
 * so switching a gram row to `kcal` is an ordinary edit with no conversion
 * behind it. Keeping `dbUnit` stored the calorie number under `g`.
 */
const nutritionValueTargetUnit = (
  uiUnit: MeasureUnit,
  storedUnit?: MeasureUnit,
) =>
  storedUnit && canConvertUnits(uiUnit, storedUnit)
    ? storedUnit
    : nutritionValueDbUnit(uiUnit);

const NUTRITION_VALUE_FORMAT = ConfigurableFormat.DECIMAL2;

export function createGroceryApiArgs(
  args: Omit<CreateGroceryArgs, 'coverSource' | 'locale' | 'organizationId'> & {
    coverSource?: null | string;
  },
) {
  return mapGroceryToApi(args);
}

export function mapGroceryToApi(
  grocery: Omit<
    CreateGroceryArgs,
    'coverSource' | 'locale' | 'organizationId'
  > & { coverSource?: null | string },
) {
  const tags: string[] = Object.values(grocery.categories ?? {}).flatMap(
    (tagList) => tagList?.map((t) => t.value) ?? [],
  );

  return omit(
    {
      ...grocery,
      configuration: { isOrg: grocery.isOrg },
      nutritionValues: grocery.nutritionValues.map((nv) => {
        const uiUnit = (nv.unit?.[0]?.value ?? 'g') as MeasureUnit;
        // The unit the row lands in — see `nutritionValueTargetUnit`.
        const dbUnit = nutritionValueTargetUnit(
          uiUnit,
          nv.dbUnit as MeasureUnit | undefined,
        );
        return {
          name: nv.name,
          unit: dbUnit,
          value: +getConvertedValueForDB({
            configurableFormat: NUTRITION_VALUE_FORMAT,
            dbUnit,
            uiConfigurableUnit: uiUnit,
            value: nv.value,
          }),
        };
      }),
      tags,
      translations: grocery.translations.map((t) => ({
        description: t.description,
        locale: t.locale?.[0]?.value,
        name: t.name,
      })),
    },
    [
      'categories',
      'isOrg',
      'mealId',
      // the shared sharing column writes these into every content form; the
      // grocery DTO is strict and a grocery is never sold
      'marketplaceCurrency',
      'marketplaceListedPrice',
    ],
  );
}
